// =====================================================================================
// INVRT Radar — web console — account — API keys tab
//
// docs/API-CONTRACTS-C1.md §2 (api/routes/keys.ts): list, create, rotate, revoke. The
// page shell registers it under the tab id "keys".
//
// WHAT A KEY IS HERE. The list is the server's own Key rows — label, display prefix,
// scopes, daily cap, status, last use, dates — and never the secret: the server stores a
// hash and hands the plaintext back exactly once, on create and on rotate. That one
// showing is the OneTimeSecretCard at the top of the panel; when it is dismissed, the
// value is gone and the list (re-read from the server) never carries it.
//
// THE BODY IS EXACT. POST /keys is validated `.strict()` server-side precisely so a
// misspelt field can never mint an unscoped key by accident, so this panel sends the
// three contract fields and nothing else: `scopes` always (an empty list IS the
// "unscoped, full access" meaning, stated on the form rather than hidden behind an
// omission), `daily_cap_credits` only when a figure was typed. The cap is passed through
// as typed; the database holds and enforces it. Rotate and revoke send no body.
//
// GATING. Reads are open to every member; create, rotate and revoke are offered only to
// an organisation admin (`activeMembership.is_org_admin`, as SitesPage does). The server
// enforces the same rule; this only shows or hides.
// =====================================================================================

import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatCreditsWithUnit, formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  CreateKeyRequest,
  CreateKeyResponse,
  Key,
  KeyList,
  RevokeKeyResponse,
  RotateKeyResponse,
  Scope,
} from "@/lib/types.c1";
import { OneTimeSecretCard } from "./delivery/OneTimeSecretCard";

/**
 * The closed scope vocabulary (_shared/scopes.ts), each with what it lets a key do, in
 * plain words. Order is the order the form shows them: the everyday ones first.
 */
const SCOPES: ReadonlyArray<{ scope: Scope; description: string }> = [
  { scope: "sites:write", description: "Add sites and change their settings, plans and exclusions." },
  { scope: "webhooks:write", description: "Add, change, test and remove webhook endpoints." },
  {
    scope: "delivery:write",
    description: "Add, change and test delivery targets, and push contacts to them.",
  },
  { scope: "contacts:read", description: "Read, search and export contacts." },
  { scope: "consent:write", description: "Publish the consent banner for a site." },
  { scope: "members:write", description: "Invite, promote, demote and remove team members." },
  { scope: "keys:write", description: "Create, rotate and revoke API keys." },
  { scope: "billing:write", description: "Start a credit purchase." },
  { scope: "tenants:write", description: "Provision tenant organisations. Partner accounts only." },
];

const keysQueryKey = (orgId: string | null) => ["keys", orgId] as const;

/** The label a row is known by in controls and confirmations; pre-C1 rows may have none. */
function keyName(key: Key): string {
  return key.label && key.label.trim() !== "" ? key.label : `Key ${key.key_prefix}`;
}

interface IssuedKey {
  plaintext: string;
  label: string;
}

// ---------------------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------------------

function CreateKeyDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (issued: IssuedKey) => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const idBase = useId();
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<Set<Scope>>(new Set());
  const [cap, setCap] = useState("");
  const [capError, setCapError] = useState<string | undefined>(undefined);

  const create = useMutation({
    mutationFn: (body: CreateKeyRequest) => api.post<CreateKeyResponse>("/keys", body),
    onSuccess: (response, body) => {
      void queryClient.invalidateQueries({ queryKey: keysQueryKey(orgId) });
      onIssued({ plaintext: response.plaintext_key, label: body.label });
      onClose();
    },
  });

  function toggleScope(scope: Scope, checked: boolean): void {
    setScopes((current) => {
      const next = new Set(current);
      if (checked) next.add(scope);
      else next.delete(scope);
      return next;
    });
  }

  function submit(): void {
    const body: CreateKeyRequest = {
      label: label.trim(),
      // Vocabulary order, whatever order the boxes were ticked in.
      scopes: SCOPES.map((entry) => entry.scope).filter((scope) => scopes.has(scope)),
    };
    const capText = cap.trim();
    if (capText !== "") {
      const figure = Number(capText);
      if (!Number.isFinite(figure) || figure < 0) {
        setCapError("Enter a number of credits, 0 or more, or leave the cap blank.");
        return;
      }
      body.daily_cap_credits = figure;
    }
    setCapError(undefined);
    create.mutate(body);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Create an API key"
      description="The key is shown once, on the next screen. Give it the narrowest scopes it needs."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={create.isPending} disabled={label.trim() === ""}>
            Create key
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={80}
          placeholder="Zapier, CI deploy, the CRM sync…"
          hint="What this key is for, so you recognise it in the list later."
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">Scopes</legend>
          <p className="text-xs text-ink-muted">
            Tick what the key may write. Read routes accept any active key. With no scope
            ticked, the key can do everything your organisation can do.
          </p>
          {SCOPES.map((entry) => {
            const id = `${idBase}-${entry.scope.replace(":", "-")}`;
            return (
              <label key={entry.scope} htmlFor={id} className="flex items-start gap-2 text-sm">
                <input
                  id={id}
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary"
                  checked={scopes.has(entry.scope)}
                  onChange={(event) => toggleScope(entry.scope, event.target.checked)}
                />
                <span>
                  <code className="font-mono text-xs text-ink">{entry.scope}</code>
                  <span className="block text-xs text-ink-muted">{entry.description}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <Input
          label="Daily cap (credits, optional)"
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          value={cap}
          onChange={(event) => setCap(event.target.value)}
          hint="The most this key may spend in one day. Leave blank and only your organisation's own cap applies."
          {...(capError ? { error: capError } : {})}
        />

        {create.isError ? <ErrorState error={create.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// Rotate / revoke
// ---------------------------------------------------------------------------------------

function RotateKeyDialog({
  target,
  onClose,
  onIssued,
}: {
  target: Key;
  onClose: () => void;
  onIssued: (issued: IssuedKey) => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();

  const rotate = useMutation({
    mutationFn: () => api.post<RotateKeyResponse>(`/keys/${target.id}/rotate`),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: keysQueryKey(orgId) });
      onIssued({ plaintext: response.plaintext_key, label: keyName(target) });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Rotate this key?"
      description={keyName(target)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={rotate.isPending}>
            Cancel
          </Button>
          <Button onClick={() => rotate.mutate()} loading={rotate.isPending}>
            Rotate key
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          A replacement key is issued with the same label, scopes and daily cap, and the
          old key stops working at once. Anything still sending the old key fails until you
          update it with the new one, which is shown once on the next screen.
        </p>
        {rotate.isError ? <ErrorState error={rotate.error} /> : null}
      </div>
    </Dialog>
  );
}

function RevokeKeyDialog({ target, onClose }: { target: Key; onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();

  const revoke = useMutation({
    mutationFn: () => api.delete<RevokeKeyResponse>(`/keys/${target.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keysQueryKey(orgId) });
      push({
        tone: "success",
        title: "Key revoked",
        body: `${keyName(target)} no longer works. Revoked keys cannot be reactivated.`,
      });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Revoke this key?"
      description={keyName(target)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => revoke.mutate()} loading={revoke.isPending}>
            Revoke key
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          Anything sending this key stops working at once, and a revoked key cannot be
          reactivated. Radar cannot tell from here which of your systems hold it, so check
          before you continue. If you only need a fresh value, rotate the key instead.
        </p>
        {revoke.isError ? <ErrorState error={revoke.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------

function KeyRow({
  apiKey,
  isAdmin,
  onRotate,
  onRevoke,
}: {
  apiKey: Key;
  isAdmin: boolean;
  onRotate: (key: Key) => void;
  onRevoke: (key: Key) => void;
}) {
  const revoked = apiKey.status === "revoked";
  const name = keyName(apiKey);

  return (
    <TableRow className={cn(revoked && "opacity-60")}>
      <TableCell className="font-medium">{name}</TableCell>
      <TableCell className="font-mono text-xs text-ink-muted">{apiKey.key_prefix}</TableCell>
      <TableCell>
        {apiKey.scopes.length === 0 ? (
          <Badge tone="warning">All scopes</Badge>
        ) : (
          <span className="flex flex-wrap gap-1">
            {apiKey.scopes.map((scope) => (
              <Badge key={scope} tone="info">
                {scope}
              </Badge>
            ))}
          </span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {apiKey.daily_cap_credits === null ? (
          <span className="text-ink-muted">No key cap</span>
        ) : (
          formatCreditsWithUnit(apiKey.daily_cap_credits)
        )}
      </TableCell>
      <TableCell>
        {revoked ? (
          <div className="flex flex-col gap-1">
            <span>
              <Badge tone="neutral">Revoked</Badge>
            </span>
            <p className="whitespace-nowrap text-xs text-ink-muted">
              Revoked {formatDateTime(apiKey.revoked_at)}
            </p>
          </div>
        ) : (
          <Badge tone="success">Active</Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-muted">
        <span className="block">{apiKey.last_used_at ? formatDate(apiKey.last_used_at) : "Never"}</span>
        <span className="block text-xs">Created {formatDate(apiKey.created_at)}</span>
      </TableCell>
      {isAdmin ? (
        <TableCell className="text-right">
          {revoked ? null : (
            <span className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Rotate ${name}`}
                onClick={() => onRotate(apiKey)}
              >
                Rotate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-danger hover:text-danger"
                aria-label={`Revoke ${name}`}
                onClick={() => onRevoke(apiKey)}
              >
                Revoke
              </Button>
            </span>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export function ApiKeysPanel() {
  const api = useApiClient();
  const { orgId, activeMembership } = useSession();
  const isAdmin = activeMembership?.is_org_admin === true;

  const [creating, setCreating] = useState(false);
  const [rotating, setRotating] = useState<Key | null>(null);
  const [revoking, setRevoking] = useState<Key | null>(null);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const keys = useQuery({
    queryKey: keysQueryKey(orgId),
    queryFn: () => api.get<KeyList>("/keys"),
    enabled: Boolean(orgId),
  });

  return (
    <div className="flex flex-col gap-4">
      {issued ? (
        <OneTimeSecretCard
          title="Copy your key now"
          noun="key"
          secret={issued.plaintext}
          subject={issued.label}
          onDone={() => setIssued(null)}
        />
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>API keys</CardTitle>
            <CardDescription className="mt-1">
              The credentials your own systems and agents use to call Radar. Send one as
              the X-API-Key header. Each key is shown once, when it is created or rotated.
            </CardDescription>
          </div>
          {isAdmin ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Create key
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {!isAdmin ? (
            <p className="text-sm text-ink-muted">
              Only an organisation administrator can create, rotate or revoke keys.
            </p>
          ) : null}

          {keys.isLoading ? <LoadingState label="Loading your keys…" /> : null}
          {keys.isError ? (
            <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />
          ) : null}

          {keys.isSuccess ? (
            keys.data.keys.length === 0 ? (
              <EmptyState
                title="No API keys yet"
                body={
                  isAdmin
                    ? "Create a key to let your own systems call Radar. You choose its scopes and an optional daily cap."
                    : "An administrator on your team can create one. Keys your organisation has minted appear here."
                }
                {...(isAdmin
                  ? { action: <Button onClick={() => setCreating(true)}>Create key</Button> }
                  : {})}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Label</TableHeaderCell>
                    <TableHeaderCell>Prefix</TableHeaderCell>
                    <TableHeaderCell>Scopes</TableHeaderCell>
                    <TableHeaderCell>Daily cap</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Last used</TableHeaderCell>
                    {isAdmin ? (
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.data.keys.map((key) => (
                    <KeyRow
                      key={key.id}
                      apiKey={key}
                      isAdmin={isAdmin}
                      onRotate={setRotating}
                      onRevoke={setRevoking}
                    />
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      {creating ? (
        <CreateKeyDialog onClose={() => setCreating(false)} onIssued={setIssued} />
      ) : null}
      {rotating ? (
        <RotateKeyDialog
          target={rotating}
          onClose={() => setRotating(null)}
          onIssued={setIssued}
        />
      ) : null}
      {revoking ? <RevokeKeyDialog target={revoking} onClose={() => setRevoking(null)} /> : null}
    </div>
  );
}
